import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

const STADIUM = "https://images.unsplash.com/photo-1665413811870-5b29a250f64a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxzdGFkaXVtJTIwbmlnaHQlMjBmbG9vZGxpZ2h0cyUyMGZvb3RiYWxsJTIwcGl0Y2h8ZW58MHx8fGJsYWNrfDE3OTE4OTE1NDJ8MA&ixlib=rb-4.1.0&q=85";

const DIAL_CODES = [
  { code: "39", label: "🇮🇹 +39 Italia" },
  { code: "34", label: "🇪🇸 +34 Spagna" },
  { code: "33", label: "🇫🇷 +33 Francia" },
  { code: "49", label: "🇩🇪 +49 Germania" },
  { code: "44", label: "🇬🇧 +44 Regno Unito" },
  { code: "41", label: "🇨🇭 +41 Svizzera" },
  { code: "351", label: "🇵🇹 +351 Portogallo" },
  { code: "32", label: "🇧🇪 +32 Belgio" },
  { code: "31", label: "🇳🇱 +31 Paesi Bassi" },
  { code: "43", label: "🇦🇹 +43 Austria" },
  { code: "1", label: "🇺🇸 +1 USA/Canada" },
  { code: "40", label: "🇷🇴 +40 Romania" },
  { code: "355", label: "🇦🇱 +355 Albania" },
  { code: "212", label: "🇲🇦 +212 Marocco" },
];

function Shell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={STADIUM} alt="Stadio" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/70 to-ink-900/40" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <h2 className="font-heading text-4xl font-black uppercase text-white leading-tight">Entra nella<br /><span className="gold-gradient-text">Challenge</span></h2>
          <p className="text-slate-300 mt-3 max-w-sm">Pronostica, scala la classifica e vinci premi esclusivi ogni 30 giorni.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-ink-900">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block mb-8"><Logo size="lg" /></Link>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      login(data.token, data.user);
      toast.success(`Bentornato, ${data.user.nickname}!`);
      nav(data.user.role === "superadmin" ? "/admin" : "/profilo");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <Shell>
      <h1 className="font-heading text-3xl font-black uppercase text-white">Accedi</h1>
      <p className="text-muted-foreground text-sm mt-1 mb-6">Bentornato nella Challenge.</p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="login-email" type="email" required placeholder="Email" className="input-dark" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="login-password" type="password" required placeholder="Password" className="input-dark" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <div className="text-right"><Link to="/recupera-password" className="text-sm text-gold-300 hover:underline">Password dimenticata?</Link></div>
        <button data-testid="login-submit" disabled={loading} className="btn-gold w-full !py-3.5">{loading ? "Accesso…" : "Accedi"}</button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">Non hai un account? <Link to="/registrati" className="text-gold-300 hover:underline">Registrati</Link></p>
    </Shell>
  );
}

export function Register() {
  const { login } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", nickname: "", password: "", dial_code: "39", whatsapp: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      login(data.token, data.user);
      toast.success("Registrazione completata! Attiva l'abbonamento per partecipare.");
      nav("/abbonamento");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <Shell>
      <h1 className="font-heading text-3xl font-black uppercase text-white">Registrati</h1>
      <p className="text-muted-foreground text-sm mt-1 mb-6">Crea il tuo account in pochi secondi.</p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="register-name" required placeholder="Nome e cognome" className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="register-email" type="email" required placeholder="Email" className="input-dark" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="register-nickname" required placeholder="Nickname (univoco)" className="input-dark" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} />
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wide">Numero WhatsApp</label>
          <div className="flex gap-2 mt-1">
            <select data-testid="register-dialcode" className="input-dark !w-auto" value={form.dial_code} onChange={(e) => setForm({ ...form, dial_code: e.target.value })}>
              {DIAL_CODES.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
            </select>
            <input data-testid="register-whatsapp" required inputMode="tel" placeholder="es. 347 1234567" className="input-dark flex-1" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          </div>
        </div>
        <input data-testid="register-password" type="password" required placeholder="Password (min. 6 caratteri)" className="input-dark" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <div className="flex items-start gap-2 bg-status-locked/10 border border-status-locked/40 rounded-lg p-3 text-sm text-status-locked" data-testid="register-payment-warning">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <span><b>Attenzione:</b> non effettuare nessun pagamento di abbonamento fino a comunicazione dell'amministratore.</span>
        </div>
        <button data-testid="register-submit" disabled={loading} className="btn-gold w-full !py-3.5">{loading ? "Creazione…" : "Crea account"}</button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">Hai già un account? <Link to="/login" className="text-gold-300 hover:underline">Accedi</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Shell>
      <h1 className="font-heading text-3xl font-black uppercase text-white">Recupera Password</h1>
      {sent ? (
        <p className="text-muted-foreground mt-4">Se l'email è registrata, riceverai un link per reimpostare la password. Controlla la tua casella.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4 mt-6">
          <input data-testid="forgot-email" type="email" required placeholder="La tua email" className="input-dark" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button data-testid="forgot-submit" className="btn-gold w-full !py-3.5">Invia link di recupero</button>
        </form>
      )}
      <p className="text-sm text-muted-foreground mt-6 text-center"><Link to="/login" className="text-gold-300 hover:underline">Torna all'accesso</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token, password }); toast.success("Password aggiornata!"); nav("/login"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Shell>
      <h1 className="font-heading text-3xl font-black uppercase text-white">Nuova Password</h1>
      <form onSubmit={submit} className="space-y-4 mt-6">
        <input data-testid="reset-password" type="password" required placeholder="Nuova password (min. 6)" className="input-dark" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="reset-submit" className="btn-gold w-full !py-3.5">Reimposta password</button>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const token = new URLSearchParams(window.location.search).get("token");
  const [state, setState] = useState("loading");
  React.useEffect(() => {
    api.post("/auth/verify-email", { token }).then(() => setState("ok")).catch(() => setState("error"));
  }, [token]);
  return (
    <Shell>
      <h1 className="font-heading text-3xl font-black uppercase text-white">Verifica Email</h1>
      <p className="text-muted-foreground mt-4">
        {state === "loading" && "Verifica in corso…"}
        {state === "ok" && "Email verificata con successo! Ora puoi accedere a tutte le funzioni."}
        {state === "error" && "Link non valido o scaduto."}
      </p>
      <Link to="/login" className="btn-gold inline-flex mt-6">Vai all'accesso</Link>
    </Shell>
  );
}
